import { useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { FiLock, FiEye, FiEyeOff, FiCheckCircle } from "react-icons/fi";
import { useApp } from "../context/AppContext";
import userService from "../services/userService";

const ROLE_HOME = {
    superadmin: "/superadmin",
    admin: "/dashboard",
    teacher: "/teacher",
    student: "/student",
    parent: "/parent",
    accountant: "/accountant",
    librarian: "/librarian",
    warden: "/warden",
    clerk: "/clerk",
    storekeeper: "/storekeeper",
};

export default function ChangePassword() {
    const navigate = useNavigate();
    const { user, mustChangePw, clearMustChangePw, role } = useApp();

    const [current, setCurrent] = useState("");
    const [next, setNext] = useState("");
    const [confirm, setConfirm] = useState("");
    const [showCurrent, setShowCurrent] = useState(false);
    const [showNext, setShowNext] = useState(false);
    const [loading, setLoading] = useState(false);

    // Password strength
    const strength = (() => {
        if (!next) return 0;
        let s = 0;
        if (next.length >= 6) s++;
        if (next.length >= 10) s++;
        if (/[A-Z]/.test(next)) s++;
        if (/[0-9]/.test(next)) s++;
        if (/[^A-Za-z0-9]/.test(next)) s++;
        return s;
    })();

    const strengthLabel =
        ["", "Weak", "Fair", "Good", "Strong", "Very Strong"][strength] || "";
    const strengthColor =
        ["", "#dc2626", "#d97706", "#003b67", "#16a34a", "#059669"][strength] ||
        "#dc2626";

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!current) {
            toast.error("Enter your current password.");
            return;
        }
        if (next.length < 6) {
            toast.error("New password must be at least 6 characters.");
            return;
        }
        if (next !== confirm) {
            toast.error("Passwords do not match.");
            return;
        }
        if (next === current) {
            toast.error("New password must differ from current password.");
            return;
        }

        setLoading(true);
        try {
            await userService.changePassword(current, next);
            toast.success("Password changed successfully.");
            clearMustChangePw();
            navigate(ROLE_HOME[role] || "/dashboard", { replace: true });
        } catch (err) {
            toast.error(err.response?.data?.message || "Failed to change password.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            style={{
                minHeight: "100vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "var(--bg)",
                padding: "1rem",
            }}
        >
            <div style={{ width: "100%", maxWidth: 420 }}>
                {/* Header */}
                <div style={{ textAlign: "center", marginBottom: "1.75rem" }}>
                    <div
                        style={{
                            width: 52,
                            height: 52,
                            borderRadius: 14,
                            background: mustChangePw ? "#fef3c7" : "var(--primary-light)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            margin: "0 auto 1rem",
                        }}
                    >
                        <FiLock
                            size={24}
                            color={mustChangePw ? "#d97706" : "var(--primary)"}
                        />
                    </div>
                    <h1 style={{ fontSize: 19, fontWeight: 700, marginBottom: 4 }}>
                        {mustChangePw ? "Set Your Password" : "Change Password"}
                    </h1>
                    <p
                        style={{
                            fontSize: 13,
                            color: "var(--text-muted)",
                            lineHeight: 1.5,
                        }}
                    >
                        {mustChangePw
                            ? "This is your first login. Please set a new password before continuing."
                            : `Changing password for ${user?.displayName || user?.username}`}
                    </p>
                </div>

                <div className="card" style={{ padding: "1.75rem" }}>
                    {mustChangePw && (
                        <div
                            style={{
                                background: "#fef3c7",
                                border: "1px solid #fcd34d",
                                borderRadius: 8,
                                padding: "0.75rem 1rem",
                                marginBottom: "1.25rem",
                                fontSize: 13,
                                color: "#92400e",
                            }}
                        >
                            ⚠ You must set a new password to continue.
                        </div>
                    )}

                    <form onSubmit={handleSubmit}>
                        {/* Current / temporary password */}
                        <div className="form-group" style={{ marginBottom: "1rem" }}>
                            <label className="form-label">
                                {mustChangePw
                                    ? "Temporary / Default Password"
                                    : "Current Password"}
                            </label>
                            <div style={{ position: "relative" }}>
                                <input
                                    type={showCurrent ? "text" : "password"}
                                    className="form-control"
                                    style={{ paddingRight: 40 }}
                                    placeholder={
                                        mustChangePw
                                            ? "Enter the password given by admin"
                                            : "Enter current password"
                                    }
                                    value={current}
                                    onChange={(e) => setCurrent(e.target.value)}
                                    autoFocus
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowCurrent((p) => !p)}
                                    style={{
                                        position: "absolute",
                                        right: 10,
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                        background: "none",
                                        border: "none",
                                        cursor: "pointer",
                                        color: "var(--text-muted)",
                                        padding: 4,
                                    }}
                                >
                                    {showCurrent ? <FiEyeOff size={14} /> : <FiEye size={14} />}
                                </button>
                            </div>
                        </div>

                        {/* New password */}
                        <div className="form-group" style={{ marginBottom: "0.5rem" }}>
                            <label className="form-label">New Password</label>
                            <div style={{ position: "relative" }}>
                                <input
                                    type={showNext ? "text" : "password"}
                                    className="form-control"
                                    style={{ paddingRight: 40 }}
                                    placeholder="Min. 6 characters"
                                    value={next}
                                    onChange={(e) => setNext(e.target.value)}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowNext((p) => !p)}
                                    style={{
                                        position: "absolute",
                                        right: 10,
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                        background: "none",
                                        border: "none",
                                        cursor: "pointer",
                                        color: "var(--text-muted)",
                                        padding: 4,
                                    }}
                                >
                                    {showNext ? <FiEyeOff size={14} /> : <FiEye size={14} />}
                                </button>
                            </div>
                        </div>

                        {/* Strength bar */}
                        {next && (
                            <div style={{ marginBottom: "1rem" }}>
                                <div
                                    style={{
                                        height: 4,
                                        borderRadius: 4,
                                        background: "var(--border)",
                                        overflow: "hidden",
                                        marginBottom: 4,
                                    }}
                                >
                                    <div
                                        style={{
                                            height: "100%",
                                            borderRadius: 4,
                                            width: `${(strength / 5) * 100}%`,
                                            background: strengthColor,
                                            transition: "width 0.3s",
                                        }}
                                    />
                                </div>
                                <div
                                    style={{
                                        fontSize: 11.5,
                                        color: strengthColor,
                                        fontWeight: 600,
                                    }}
                                >
                                    {strengthLabel}
                                </div>
                            </div>
                        )}

                        {/* Confirm */}
                        <div className="form-group" style={{ marginBottom: "1.5rem" }}>
                            <label className="form-label">Confirm New Password</label>
                            <div style={{ position: "relative" }}>
                                <input
                                    type="password"
                                    className={`form-control ${confirm && confirm !== next ? "error" : ""}`}
                                    placeholder="Re-enter new password"
                                    value={confirm}
                                    onChange={(e) => setConfirm(e.target.value)}
                                />
                                {confirm && confirm === next && (
                                    <FiCheckCircle
                                        size={15}
                                        color="#16a34a"
                                        style={{
                                            position: "absolute",
                                            right: 10,
                                            top: "50%",
                                            transform: "translateY(-50%)",
                                        }}
                                    />
                                )}
                            </div>
                            {confirm && confirm !== next && (
                                <span className="form-error">Passwords do not match.</span>
                            )}
                        </div>

                        <button
                            type="submit"
                            className="btn btn-primary"
                            style={{
                                width: "100%",
                                justifyContent: "center",
                                padding: "0.65rem",
                            }}
                            disabled={loading}
                        >
                            {loading ? "Changing…" : "Change Password"}
                        </button>

                        {/* Skip link — only if NOT forced */}
                        {!mustChangePw && (
                            <button
                                type="button"
                                onClick={() => navigate(-1)}
                                style={{
                                    width: "100%",
                                    marginTop: "0.75rem",
                                    background: "none",
                                    border: "none",
                                    fontSize: 13,
                                    color: "var(--text-muted)",
                                    cursor: "pointer",
                                }}
                            >
                                Cancel
                            </button>
                        )}
                    </form>
                </div>
            </div>
        </div>
    );
}
